<template>
  <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style="max-height: 92vh;">

      <!-- Cabecera modal -->
      <div class="flex items-center justify-between px-6 py-4 border-b shrink-0">
        <div>
          <h2 class="text-lg font-bold text-gray-900">Nota de Venta</h2>
          <span class="text-sm text-gray-500">{{ nota.folio }}</span>
        </div>
        <div class="flex gap-2 items-center">
          <span :class="badgeEstado(nota.estado)" class="text-xs font-semibold px-2.5 py-1 rounded-full">
            {{ labelEstado(nota.estado) }}
          </span>

          <!-- Botón Facturar — solo cuando la nota está activa -->
          <button
            v-if="nota.estado === 'activa'"
            @click="abrirModalFacturar"
            class="px-4 py-2 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition flex items-center gap-1.5"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            Facturar
          </button>

          <!-- Accesos a factura ya timbrada -->
          <template v-if="nota.estado === 'facturada' && factura">
            <a
              v-if="factura.pdf_url"
              :href="factura.pdf_url"
              target="_blank"
              class="px-3 py-2 rounded bg-red-100 text-red-700 text-sm font-semibold hover:bg-red-200 transition flex items-center gap-1.5"
            >
              PDF
            </a>
            <button
              @click="descargarXml"
              class="px-3 py-2 rounded bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition flex items-center gap-1.5"
            >
              XML
            </button>
          </template>

          <button @click="imprimir" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
            </svg>
            Imprimir
          </button>
          <button @click="$emit('cerrar')" class="ml-1 text-gray-400 hover:text-gray-600 p-1">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Área imprimible -->
      <div ref="printArea" class="flex-1 overflow-y-auto p-6 bg-white">

        <h2 class="text-xl font-bold text-gray-900 mb-4">Nota de Venta</h2>

        <!-- Datos Generales -->
        <section class="border border-gray-300 rounded mb-5">
          <div class="px-4 py-2 border-b border-gray-300 bg-gray-50">
            <span class="text-sm font-semibold text-gray-700">Datos Generales</span>
          </div>
          <div class="p-4">
            <!-- Fila 1: Folio Nota / Vendedor / Cliente -->
            <div class="grid grid-cols-3 gap-4 mb-3 text-sm">
              <div>
                <span class="text-gray-500">Folio</span>
                <span class="ml-2 font-bold text-gray-900">{{ nota.folio }}</span>
              </div>
              <div>
                <span class="text-gray-500">Vendedor</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.vendedor ?? '—' }}</span>
              </div>
              <div>
                <span class="text-gray-500">Cliente</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.cliente ?? '—' }}</span>
              </div>
            </div>

            <!-- Fila 2: Código de barras / Pedido origen / Fechas -->
            <div class="grid grid-cols-3 gap-4 mb-3 items-center text-sm">
              <div>
                <span class="block text-gray-500 mb-1">Código</span>
                <svg ref="barcodeEl" class="h-10"></svg>
              </div>
              <div>
                <span class="text-gray-500">Pedido Origen</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.folio ?? '—' }}</span>
              </div>
              <div class="space-y-1">
                <div>
                  <span class="text-gray-500">Fecha Entrega</span>
                  <span class="ml-2 font-bold text-gray-900">{{ formatFecha(header.fecha_entrega) }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Fecha Nota</span>
                  <span class="ml-2 font-bold text-gray-900">{{ formatFecha(nota.created_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Fila 3: Diseñador / Tipo Entrega -->
            <div class="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span class="text-gray-500">Diseñador</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.disenador ?? '—' }}</span>
              </div>
              <div></div>
              <div>
                <span class="text-gray-500">Tipo Entrega</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.tipo_entrega === 'recepcion' ? 'Recepción' : 'Domicilio' }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Detalle de Venta -->
        <section class="border border-gray-300 rounded mb-5">
          <div class="px-4 py-2 border-b border-gray-300 bg-gray-50">
            <span class="text-sm font-semibold text-gray-700">Detalle de Venta</span>
          </div>
          <table class="w-full text-xs text-left">
            <thead>
              <tr class="border-b border-gray-300 bg-gray-50">
                <th class="px-3 py-2 font-semibold text-gray-700">Producto</th>
                <th class="px-3 py-2 font-semibold text-gray-700 text-center">Cantidad</th>
                <th class="px-3 py-2 font-semibold text-gray-700 text-center">Ancho</th>
                <th class="px-3 py-2 font-semibold text-gray-700 text-center">Alto</th>
                <th class="px-3 py-2 font-semibold text-gray-700">Archivo</th>
                <th class="px-3 py-2 font-semibold text-gray-700">Acabados</th>
                <th class="px-3 py-2 font-semibold text-gray-700">Frente/Vuelta</th>
                <th class="px-3 py-2 font-semibold text-gray-700 text-right">Precio Pieza</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(det, i) in detalles" :key="i" class="border-t border-gray-200">
                <td class="px-3 py-2 font-medium text-gray-800">{{ det.producto_nombre_orig }}</td>
                <td class="px-3 py-2 text-center">{{ det.cantidad_piezas }}</td>
                <td class="px-3 py-2 text-center">{{ det.ancho_metros ?? '—' }}</td>
                <td class="px-3 py-2 text-center">{{ det.alto_metros ?? '—' }}</td>
                <td class="px-3 py-2">{{ det.nombre_archivo || '—' }}</td>
                <td class="px-3 py-2">{{ det.acabados || '—' }}</td>
                <td class="px-3 py-2">{{ det.frente_vuelta ?? '-' }}</td>
                <td class="px-3 py-2 text-right font-semibold text-gray-800">{{ formatMoney(det.precio_pieza) }}</td>
              </tr>
              <tr v-if="detalles.length === 0">
                <td colspan="8" class="px-4 py-6 text-center text-gray-400">Sin detalles</td>
              </tr>
            </tbody>
            <tfoot v-if="detalles.length > 0">
              <tr class="border-t-2 border-gray-300 bg-gray-50">
                <td colspan="7" class="px-3 py-2 text-right text-sm font-semibold text-gray-700">Total:</td>
                <td class="px-3 py-2 text-right text-sm font-bold text-violet-700">{{ formatMoney(totalVenta) }}</td>
              </tr>
            </tfoot>
          </table>
        </section>

        <!-- Observaciones -->
        <section v-if="detalles.some(d => d.comentarios)" class="border border-gray-300 rounded">
          <div class="px-4 py-2 border-b border-gray-300 bg-gray-50">
            <span class="text-sm font-semibold text-gray-700">Observaciones</span>
          </div>
          <div class="p-4 space-y-1">
            <div v-for="(det, i) in detalles.filter(d => d.comentarios)" :key="i" class="text-sm text-gray-700">
              <span class="font-medium">{{ det.producto_nombre_orig }}:</span> {{ det.comentarios }}
            </div>
          </div>
        </section>

      </div>
    </div>
  </div>

  <!-- ── Modal Facturar (encima del modal de la nota) ───────────────────── -->
  <div v-if="modalFacturar.visible" class="fixed inset-0 flex items-center justify-center bg-black/70 p-4" style="z-index: 60;">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style="max-height: 92vh;">
      <div class="flex items-center justify-between px-6 py-4 border-b shrink-0">
        <div class="flex items-center gap-3">
          <h3 class="text-base font-bold text-gray-900">Timbrar Factura CFDI 4.0 (SW Sandbox)</h3>
          <span
            v-if="modalFacturar.resultado"
            :class="modalFacturar.resultado.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
            class="text-xs font-semibold px-2 py-0.5 rounded-full"
          >
            {{ modalFacturar.resultado.ok ? 'OK' : 'ERROR' }}
          </span>
          <span v-if="modalFacturar.resultado?.httpStatus" class="text-xs font-mono text-gray-500">HTTP {{ modalFacturar.resultado.httpStatus }}</span>
        </div>
        <button @click="cerrarModalFacturar" class="text-gray-400 hover:text-gray-600 p-1">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <p class="text-sm text-gray-500">Nota: <span class="font-semibold text-gray-800">{{ nota.folio }}</span></p>

        <!-- Error -->
        <div v-if="modalFacturar.error" class="px-3 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
          {{ modalFacturar.error }}
        </div>

        <!-- Formulario (cuando aún no se ha timbrado) -->
        <template v-if="!modalFacturar.resultado">
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Uso CFDI</label>
            <input
              value="S01 – Sin efectos fiscales (fijo en sandbox)"
              disabled
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-gray-100 text-gray-500"
            />
            <p class="text-xs text-gray-500 mt-1">Para receptor con régimen 616 (Público en General) el SAT solo permite S01.</p>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Forma de Pago <span class="text-red-500">*</span></label>
            <select v-model="modalFacturar.forma_pago" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300">
              <option value="">Selecciona...</option>
              <option value="01">01 – Efectivo</option>
              <option value="02">02 – Cheque nominativo</option>
              <option value="03">03 – Transferencia electrónica</option>
              <option value="04">04 – Tarjeta de crédito</option>
              <option value="28">28 – Tarjeta de débito</option>
              <option value="99">99 – Por definir</option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Método de Pago <span class="text-red-500">*</span></label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input type="radio" v-model="modalFacturar.metodo_pago" value="PUE" class="accent-green-600" />
                <span><strong>PUE</strong> – Pago en una sola exhibición</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input type="radio" v-model="modalFacturar.metodo_pago" value="PPD" class="accent-green-600" />
                <span><strong>PPD</strong> – Pago en parcialidades</span>
              </label>
            </div>
          </div>

          <div class="px-3 py-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded">
            <strong>Modo prueba:</strong> se enviará al sandbox de SmarterWeb con RFCs genéricos del SAT
            (Emisor <code>EKU9003173C9</code>, Receptor <code>XAXX010101000</code>) y los conceptos reales de esta nota.
          </div>
        </template>

        <!-- Resultado -->
        <template v-if="modalFacturar.resultado">
          <div v-if="modalFacturar.resultado.requestUrl">
            <p class="text-xs font-bold uppercase text-gray-500 mb-1">Endpoint</p>
            <code class="block px-3 py-2 bg-gray-100 rounded text-xs font-mono break-all">{{ modalFacturar.resultado.requestUrl }}</code>
          </div>

          <div v-if="modalFacturar.resultado.requestPayload">
            <p class="text-xs font-bold uppercase text-gray-500 mb-1">Request Body</p>
            <pre class="px-3 py-2 bg-gray-100 rounded text-xs font-mono overflow-x-auto max-h-56 text-gray-800">{{ JSON.stringify(modalFacturar.resultado.requestPayload, null, 2) }}</pre>
          </div>

          <div v-if="modalFacturar.resultado.responseBody || modalFacturar.resultado.responseRaw">
            <p class="text-xs font-bold uppercase text-gray-500 mb-1">Response</p>
            <pre class="px-3 py-2 bg-gray-100 rounded text-xs font-mono overflow-x-auto max-h-72 text-gray-800">{{ modalFacturar.resultado.responseBody ? JSON.stringify(modalFacturar.resultado.responseBody, null, 2) : modalFacturar.resultado.responseRaw }}</pre>
          </div>
        </template>
      </div>

      <div class="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
        <button @click="cerrarModalFacturar" class="px-4 py-2 rounded bg-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-300 transition">
          {{ modalFacturar.resultado ? 'Cerrar' : 'Cancelar' }}
        </button>
        <button
          v-if="!modalFacturar.resultado"
          @click="timbrar"
          :disabled="modalFacturar.cargando"
          class="px-5 py-2 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition disabled:opacity-60"
        >
          {{ modalFacturar.cargando ? 'Timbrando...' : 'Timbrar Factura' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import { useUserStore } from '@/stores/user.js'

const userStore = useUserStore()
const httpFactura = useHttp()

const props = defineProps({
  visible: Boolean,
  nota:    { type: Object, default: () => ({}) },
})

const emit = defineEmits(['cerrar', 'facturada'])

const printArea = ref(null)
const barcodeEl = ref(null)

// ── Facturación ───────────────────────────────────────────────────────────────
const factura = ref(null)

const modalFacturar = ref({
  visible:     false,
  cargando:    false,
  error:       '',
  uso_cfdi:    'S01',
  forma_pago:  '01',
  metodo_pago: 'PUE',
  resultado:   null,
})

const abrirModalFacturar = () => {
  modalFacturar.value = {
    visible: true, cargando: false, error: '',
    uso_cfdi: 'S01', forma_pago: '01', metodo_pago: 'PUE',
    resultado: null,
  }
}

const cerrarModalFacturar = () => {
  modalFacturar.value.visible = false
}

const timbrar = async () => {
  const { uso_cfdi, forma_pago, metodo_pago } = modalFacturar.value
  if (!uso_cfdi || !forma_pago || !metodo_pago) {
    modalFacturar.value.error = 'Completa todos los campos requeridos.'
    return
  }

  modalFacturar.value.cargando = true
  modalFacturar.value.error    = ''

  try {
    await httpFactura.post('/pos2/conectia/test', {
      nota_id:     props.nota.id,
      uso_cfdi,
      forma_pago,
      metodo_pago,
    })
    const r = httpFactura.data.value ?? {}
    modalFacturar.value.resultado = {
      ok:             !!r.ok,
      httpStatus:     r.http_status ?? null,
      requestUrl:     r.request_url ?? '',
      requestPayload: r.request_payload ?? null,
      responseBody:   r.response_body ?? null,
      responseRaw:    r.response_raw ?? '',
    }
    if (r.ok) emit('facturada', props.nota.id)
  } catch (err) {
    const r = err.response?.data ?? {}
    modalFacturar.value.resultado = {
      ok:             false,
      httpStatus:     r.http_status ?? err.response?.status ?? null,
      requestUrl:     r.request_url ?? '',
      requestPayload: r.request_payload ?? null,
      responseBody:   r.response_body ?? null,
      responseRaw:    r.response_raw ?? '',
    }
    modalFacturar.value.error = r.message ?? err.message ?? 'Error al timbrar.'
  } finally {
    modalFacturar.value.cargando = false
  }
}

const descargarXml = async () => {
  if (!factura.value?.id) return
  try {
    await httpFactura.get(`/pos2/facturas/${factura.value.id}/xml`)
    const blob = new Blob([httpFactura.data.value], { type: 'application/xml' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `${factura.value.uuid}.xml`
    a.click()
    URL.revokeObjectURL(url)
  } catch {
    alert('No se pudo descargar el XML.')
  }
}

// Cargar factura si la nota ya está facturada
watch(() => [props.visible, props.nota?.id], async ([vis]) => {
  if (vis && props.nota?.estado === 'facturada') {
    try {
      await httpFactura.get(`/pos2/notas/${props.nota.id}/factura`)
      factura.value = httpFactura.data.value
    } catch {
      factura.value = null
    }
  } else {
    factura.value = null
  }
})

const header   = computed(() => props.nota?.snapshot_header   ?? {})
const detalles = computed(() => props.nota?.snapshot_detalles ?? [])

const totalVenta = computed(() =>
  detalles.value.reduce((s, d) => s + Number(d.precio_pieza ?? 0), 0)
)

const formatFecha = (val) => {
  if (!val) return '—'
  return new Date(val).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
}

const formatMoney = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val ?? 0)

const labelEstado = (e) => ({ activa: 'Activa', facturada: 'Facturada', cancelada: 'Cancelada' }[e] ?? e)

const badgeEstado = (e) => ({
  activa:    'bg-violet-100 text-violet-700',
  facturada: 'bg-green-100 text-green-700',
  cancelada: 'bg-red-100 text-red-600',
}[e] ?? 'bg-gray-100 text-gray-600')

// ── Código de barras Code 128B del id de la nota ─────────────────────────────
const renderBarcode = () => {
  if (!barcodeEl.value || !props.nota?.id) return
  const svg    = barcodeEl.value
  const value  = String(props.nota.id).padStart(6, '0')
  const encoded = encodeCode128B(value)
  const bw = 2, bh = 40
  const totalW = encoded.length * bw + 20
  svg.setAttribute('viewBox', `0 0 ${totalW} ${bh + 10}`)
  svg.setAttribute('width', totalW)
  svg.setAttribute('height', bh + 10)
  let x = 10, rects = ''
  for (let i = 0; i < encoded.length; i++) {
    if (encoded[i] === '1') rects += `<rect x="${x}" y="0" width="${bw}" height="${bh}" fill="black"/>`
    x += bw
  }
  svg.innerHTML = rects + `<text x="${totalW / 2}" y="${bh + 9}" text-anchor="middle" font-size="7" font-family="monospace" fill="#333">${value}</text>`
}

watch(() => [props.visible, props.nota?.id], async ([vis]) => {
  if (vis) {
    await nextTick()
    renderBarcode()
  }
}, { immediate: true })

function encodeCode128B(text) {
  const patterns = [
    '11011001100','11001101100','11001100110','10010011000','10010001100',
    '10001001100','10011001000','10011000100','10001100100','11001001000',
    '11001000100','11000100100','10110011100','10011011100','10011001110',
    '10111001100','10011101100','10011100110','11001110010','11001011100',
    '11001001110','11011100100','11001110100','11101101110','11101001100',
    '11100101100','11100100110','11101100100','11100110100','11100110010',
    '11011011000','11011000110','11000110110','10100011000','10001011000',
    '10001000110','10110001000','10001101000','10001100010','11010001000',
    '11000101000','11000100010','10110111000','10110001110','10001101110',
    '10111011000','10111000110','10001110110','11101110110','11010001110',
    '11000101110','11011101000','11011100010','11011101110','11101011000',
    '11101000110','11100010110','11101101000','11101100010','11100011010',
    '11101111010','11001000010','11110001010','10100110000','10100001100',
    '10010110000','10010000110','10000101100','10000100110','10110010000',
    '10110000100','10011010000','10011000010','10000110100','10000110010',
    '11000010010','11001010000','11110111010','11000010100','10001111010',
    '10100111100','10010111100','10010011110','10111100100','10011110100',
    '10011110010','11110100100','11110010100','11110010010','11011011110',
    '11011110110','11110110110','10101111000','10100011110','10001011110',
    '10111101000','10111100010','11110101000','11110100010','10111011110',
    '10111101110','11101011110','11110101110','11010000100','11010010000',
    '11010011100',
  ]
  const charVal = (ch) => ch.charCodeAt(0) - 32
  let checksum = 104
  let bars = '11010010000'
  for (let i = 0; i < text.length; i++) {
    const v = charVal(text[i])
    bars += patterns[v] ?? ''
    checksum += v * (i + 1)
  }
  bars += patterns[checksum % 103] ?? ''
  bars += '1100011101011'
  return bars
}

// ── Imprimir ─────────────────────────────────────────────────────────────────
const imprimir = () => {
  const inner = printArea.value?.innerHTML ?? ''
  const win   = window.open('', '_blank', 'width=960,height=720')
  win.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Nota de Venta — ${props.nota?.folio ?? ''}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:20px}
    h2{font-size:18px;font-weight:700;margin-bottom:14px}
    section{border:1px solid #d1d5db;border-radius:6px;margin-bottom:16px}
    .sec-hdr{background:#f9fafb;padding:7px 14px;border-bottom:1px solid #d1d5db;font-size:11px;font-weight:700}
    .grid3{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;padding:14px}
    table{width:100%;border-collapse:collapse}
    th{background:#f3f4f6;padding:5px 10px;text-align:left;font-size:10px;font-weight:700;border-bottom:1px solid #d1d5db}
    td{padding:5px 10px;border-top:1px solid #f3f4f6;font-size:11px;vertical-align:top}
    tfoot td{border-top:2px solid #d1d5db;font-weight:700}
    svg{display:block}
    @media print{body{padding:8px}}
  </style>
</head>
<body>${inner}</body>
</html>`)
  win.document.close()
  win.focus()
  setTimeout(() => win.print(), 500)
}
</script>
